import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import healthApi from '@/api/healthApi';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Droplet, Moon, Dumbbell, Pill, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import NeedHelpButton from '@/components/NeedHelpButton';
import AppFooter from '@/components/AppFooter';

const Habits = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [recentHabits, setRecentHabits] = useState([]);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    water_intake: 0,
    sleep_hours: 0,
    exercise_minutes: 0,
    medications_taken: []
  });

  useEffect(() => {
    fetchRecentHabits();
  }, []);

  const fetchRecentHabits = async () => {
    try {
      const { data } = await healthApi.getHabits(7);
      setRecentHabits(data);
      
      // Check if today's tracking exists
      const today = new Date().toISOString().split('T')[0];
      const todayHabit = data.find(h => h.date === today);
      if (todayHabit) {
        setFormData(todayHabit);
      }
    } catch (error) {
      console.error('Error fetching habits:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await healthApi.trackHabit(formData);
      toast.success('Habits tracked successfully!');
      fetchRecentHabits();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to track habits';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-healthloop-mint-50 to-teal-50 p-4" data-testid="habits-page">
      <div className="max-w-5xl mx-auto pt-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-4"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Track Today's Habits */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Track Your Habits</CardTitle>
                <CardDescription>Log your daily health habits</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="date">Date</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      max={new Date().toISOString().split('T')[0]}
                      data-testid="date-input"
                    />
                  </div>

                  <div className="space-y-4">
                    {/* Water Intake */}
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="flex items-center mb-3">
                        <Droplet className="h-6 w-6 text-blue-600 mr-3" />
                        <Label htmlFor="water" className="text-lg font-semibold">Water Intake (Liters)</Label>
                      </div>
                      <Input
                        id="water"
                        type="number"
                        step="0.1"
                        min="0"
                        max="10"
                        value={formData.water_intake}
                        onChange={(e) => setFormData({ ...formData, water_intake: parseFloat(e.target.value) || 0 })}
                        placeholder="e.g., 2.5"
                        data-testid="water-input"
                      />
                    </div>

                    {/* Sleep Hours */}
                    <div className="p-4 bg-healthloop-mint-50 rounded-lg">
                      <div className="flex items-center mb-3">
                        <Moon className="h-6 w-6 text-healthloop-mint-600 mr-3" />
                        <Label htmlFor="sleep" className="text-lg font-semibold">Sleep Hours</Label>
                      </div>
                      <Input
                        id="sleep"
                        type="number"
                        step="0.5"
                        min="0"
                        max="24"
                        value={formData.sleep_hours}
                        onChange={(e) => setFormData({ ...formData, sleep_hours: parseFloat(e.target.value) || 0 })}
                        placeholder="e.g., 7.5"
                        data-testid="sleep-input"
                      />
                    </div>

                    {/* Exercise */}
                    <div className="p-4 bg-green-50 rounded-lg">
                      <div className="flex items-center mb-3">
                        <Dumbbell className="h-6 w-6 text-green-600 mr-3" />
                        <Label htmlFor="exercise" className="text-lg font-semibold">Exercise (Minutes)</Label>
                      </div>
                      <Input
                        id="exercise"
                        type="number"
                        min="0"
                        max="1440"
                        value={formData.exercise_minutes}
                        onChange={(e) => setFormData({ ...formData, exercise_minutes: parseInt(e.target.value) || 0 })}
                        placeholder="e.g., 30"
                        data-testid="exercise-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    disabled={loading}
                    data-testid="submit-habits"
                  >
                    {loading ? 'Saving...' : 'Save Habits'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Recent History */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2" />
                  Recent Tracking
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4" data-testid="recent-habits">
                  {recentHabits.length === 0 ? (
                    <p className="text-gray-500 text-sm">No tracking history yet</p>
                  ) : (
                    recentHabits.slice(0, 5).map((habit) => (
                      <div key={habit.id} className="p-3 bg-gray-50 rounded-lg space-y-2">
                        <p className="font-semibold text-sm">{habit.date}</p>
                        <div className="space-y-1 text-xs">
                          <div className="flex items-center">
                            <Droplet className="h-3 w-3 text-blue-600 mr-1" />
                            <span>{habit.water_intake}L water</span>
                          </div>
                          <div className="flex items-center">
                            <Moon className="h-3 w-3 text-healthloop-mint-600 mr-1" />
                            <span>{habit.sleep_hours}h sleep</span>
                          </div>
                          <div className="flex items-center">
                            <Dumbbell className="h-3 w-3 text-green-600 mr-1" />
                            <span>{habit.exercise_minutes}min exercise</span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <NeedHelpButton />
      <AppFooter />
    </div>
  );
};

export default Habits;
